import React, { useEffect, useMemo, useState } from 'react';
import { rolesAPI } from '../../services/api';
import { useData } from '../../context/DataContext';
import Modal from '../../components/Modal';
import { MENU_PERMISSION_ITEMS, PERMISSION_ACTIONS } from '../../utils/permissions';
import './Roles.css';

const emptyRole = { name: '', description: '', appliesTo: ['manager', 'instructor'], permissions: [] };

const BASE_ROLE_LABELS = { manager: 'Managers', instructor: 'Instructeurs' };

const Roles = () => {
  const { data } = useData();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const list = await rolesAPI.getAll();
      setRoles(Array.isArray(list) ? list : []);
      setError('');
    } catch (err) {
      setError(err.message || 'Rollen laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const usersByRole = useMemo(() => {
    const map = new Map();
    (data.users || []).forEach((user) => {
      const roleId = String(user.accessRole?.id || '');
      if (!roleId) return;
      if (!map.has(roleId)) map.set(roleId, []);
      map.get(roleId).push(user);
    });
    return map;
  }, [data.users]);

  const permissionSet = useMemo(() => new Set(editing?.permissions || []), [editing]);

  const togglePermission = (menuKey, action) => {
    const permission = `${menuKey}:${action}`;
    setEditing((prev) => {
      const next = new Set(prev.permissions);
      if (next.has(permission)) {
        next.delete(permission);
        // Zonder lezen heeft schrijven geen zin: leesrecht intrekken haalt de rest ook weg.
        if (action === 'read') PERMISSION_ACTIONS.forEach((a) => next.delete(`${menuKey}:${a.key}`));
      } else {
        next.add(permission);
        if (action !== 'read') next.add(`${menuKey}:read`);
      }
      return { ...prev, permissions: Array.from(next) };
    });
  };

  const setRow = (menuKey, enabled) => {
    setEditing((prev) => {
      const next = new Set(prev.permissions);
      PERMISSION_ACTIONS.forEach((action) => {
        const permission = `${menuKey}:${action.key}`;
        if (enabled) next.add(permission); else next.delete(permission);
      });
      return { ...prev, permissions: Array.from(next) };
    });
  };

  const setColumn = (action, enabled) => {
    setEditing((prev) => {
      const next = new Set(prev.permissions);
      MENU_PERMISSION_ITEMS.forEach((item) => {
        if (enabled) {
          next.add(`${item.key}:${action}`);
          next.add(`${item.key}:read`);
        } else {
          next.delete(`${item.key}:${action}`);
          if (action === 'read') PERMISSION_ACTIONS.forEach((a) => next.delete(`${item.key}:${a.key}`));
        }
      });
      return { ...prev, permissions: Array.from(next) };
    });
  };

  const toggleAppliesTo = (baseRole) => {
    setEditing((prev) => {
      const current = new Set(prev.appliesTo || []);
      if (current.has(baseRole)) current.delete(baseRole); else current.add(baseRole);
      return { ...prev, appliesTo: Array.from(current) };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editing.name.trim()) {
      window.alert('Geef de rol een naam.');
      return;
    }
    if (!editing.appliesTo?.length) {
      window.alert('Kies voor wie deze rol bedoeld is (managers en/of instructeurs).');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description,
        appliesTo: editing.appliesTo,
        permissions: editing.permissions
      };
      if (editing.id) await rolesAPI.update(editing.id, payload);
      else await rolesAPI.create(payload);
      setEditing(null);
      await loadRoles();
    } catch (err) {
      window.alert(err.message || 'Rol opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role) => {
    const count = role.userCount || 0;
    const message = count
      ? `Rol "${role.name}" verwijderen? ${count} gebruiker(s) vallen terug op de standaardrechten van hun basisrol.`
      : `Rol "${role.name}" verwijderen?`;
    if (!window.confirm(message)) return;
    try {
      await rolesAPI.delete(role.id);
      await loadRoles();
    } catch (err) {
      window.alert(err.message || 'Rol verwijderen mislukt');
    }
  };

  const summarize = (role) => {
    const readable = MENU_PERMISSION_ITEMS.filter((item) => role.permissions.includes(`${item.key}:read`));
    if (!readable.length) return 'Geen menu-toegang';
    return readable.map((item) => item.label).join(', ');
  };

  return (
    <div className="roles-page">
      <div className="roles-header">
        <div>
          <h1>Rollenbeheer</h1>
          <p className="roles-muted">
            Maak toegangsrollen voor managers en instructeurs en bepaal per menu-onderdeel of ze mogen aanmaken, lezen, wijzigen of verwijderen.
            Wijs een rol toe via Gebruikers → Bewerken. Administrators hebben altijd volledige toegang; gebruikers zonder toegangsrol houden de standaardrechten van hun basisrol.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditing({ ...emptyRole })}>+ Nieuwe rol</button>
      </div>

      {error && <p className="roles-error">{error}</p>}

      <div className="roles-table-wrap">
        <table className="roles-table">
          <thead>
            <tr>
              <th>Rol</th>
              <th>Voor</th>
              <th>Menu-toegang</th>
              <th>Gebruikers</th>
              <th className="roles-actions-col">Acties</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="roles-empty">Laden…</td></tr>}
            {!loading && roles.length === 0 && (
              <tr><td colSpan={5} className="roles-empty">Nog geen rollen. Maak de eerste rol aan.</td></tr>
            )}
            {roles.map((role) => {
              const assigned = usersByRole.get(String(role.id)) || [];
              return (
                <tr key={role.id}>
                  <td>
                    <strong>{role.name}</strong>
                    {role.description && <div className="roles-muted">{role.description}</div>}
                  </td>
                  <td>{(role.appliesTo || []).map((item) => BASE_ROLE_LABELS[item]).join(', ')}</td>
                  <td className="roles-summary">{summarize(role)}</td>
                  <td>
                    <span className="roles-count" title={assigned.map((u) => u.name).join(', ')}>{role.userCount || 0}</span>
                  </td>
                  <td className="roles-actions-col">
                    <div className="roles-row-actions">
                      <button type="button" className="btn-secondary" onClick={() => setEditing({ ...emptyRole, ...role, permissions: [...(role.permissions || [])] })}>Bewerken</button>
                      <button type="button" className="btn-delete" onClick={() => handleDelete(role)}>Verwijderen</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? `Rol bewerken: ${editing.name}` : 'Nieuwe rol'}
        size="lg"
        position="right"
      >
        {editing && (
          <form onSubmit={handleSave} className="roles-form">
            <label className="roles-field">
              <span>Naam *</span>
              <input
                className="form-input"
                value={editing.name}
                onChange={(e) => setEditing((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="bijv. Manager financiën"
                required
              />
            </label>
            <label className="roles-field">
              <span>Omschrijving</span>
              <input
                className="form-input"
                value={editing.description || ''}
                onChange={(e) => setEditing((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Optioneel"
              />
            </label>

            <div className="roles-field">
              <span>Bedoeld voor *</span>
              <div className="roles-applies">
                {Object.entries(BASE_ROLE_LABELS).map(([key, label]) => (
                  <label key={key}>
                    <input type="checkbox" checked={(editing.appliesTo || []).includes(key)} onChange={() => toggleAppliesTo(key)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="roles-matrix-wrap">
              <table className="roles-matrix">
                <thead>
                  <tr>
                    <th>Menu-onderdeel</th>
                    {PERMISSION_ACTIONS.map((action) => {
                      const allOn = MENU_PERMISSION_ITEMS.every((item) => permissionSet.has(`${item.key}:${action.key}`));
                      return (
                        <th key={action.key}>
                          <label className="roles-col-toggle" title={`Alles ${action.label.toLowerCase()} aan/uit`}>
                            <input type="checkbox" checked={allOn} onChange={(e) => setColumn(action.key, e.target.checked)} />
                            {action.label}
                          </label>
                        </th>
                      );
                    })}
                    <th>Alles</th>
                  </tr>
                </thead>
                <tbody>
                  {MENU_PERMISSION_ITEMS.map((item) => {
                    const rowAll = PERMISSION_ACTIONS.every((action) => permissionSet.has(`${item.key}:${action.key}`));
                    return (
                      <tr key={item.key}>
                        <td>{item.label}</td>
                        {PERMISSION_ACTIONS.map((action) => (
                          <td key={action.key} className="roles-check">
                            <input
                              type="checkbox"
                              aria-label={`${item.label}: ${action.label}`}
                              checked={permissionSet.has(`${item.key}:${action.key}`)}
                              onChange={() => togglePermission(item.key, action.key)}
                            />
                          </td>
                        ))}
                        <td className="roles-check">
                          <input type="checkbox" aria-label={`${item.label}: alles`} checked={rowAll} onChange={(e) => setRow(item.key, e.target.checked)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <small className="roles-muted">
              "Lezen" bepaalt of het menu-onderdeel zichtbaar is. Aanmaken, wijzigen of verwijderen zet lezen automatisch aan.
              Het dashboard is altijd zichtbaar en toont alleen snelacties waarvoor de rol leesrechten heeft.
            </small>

            <div className="roles-form-actions">
              <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>Annuleren</button>
              <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Opslaan…' : 'Opslaan'}</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Roles;
